import { useState, useCallback } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography, Button, Tooltip, Paper } from '@mui/material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUIButton, SCUIDateRanger } from '../../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';
import TimeHelper from '../../../../utils/time';
import { downloadExcel } from '../../../../utils/excelFileHelper';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ShippingQuery from '../../../../services/query/shipping/ShippingQuery.services';
import { SCUINoRecordComponent } from '../../../../atomic/atoms';
import FeeManagementListDrawer from './components/FeeManagementListDrawer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { LCWFEE } from '../../lcwCargoFeeManagement.classes';
const cellTypographySx = {
    fontFamily: 'Nunito, sans-serif',
    fontSize: '11px',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    width: '100%',
} as const;

interface FeeRowItem {
    id: number;
    shippingCompanyId: number;
    shippingCompanyName: string;
    desi: number | '';
    cost: number | '';
    startDate: string | Date | null;
    endDate: string | Date | null;
    changedAt: string | Date | null;
    changedBy: string;
}

interface CargoCompanyOption {
    id: number;
    name: string;
}
export const FeeManagementListTab = ({
    setIsLoading,
    companyOptions,
}: {
    setIsLoading: (isLoading: boolean) => void;
    companyOptions: CargoCompanyOption[];
}): React.JSX.Element => {
    const { t } = useTranslation();

    const [rows, setRows] = useState<FeeRowItem[]>([]);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [formState, setFormState] = useState<FeeRowItem | null>(null);

    const [filterCompanyId, setFilterCompanyId] = useState<number>(-1);
    const [filterStart, setFilterStart] = useState<string>('');
    const [filterEnd, setFilterEnd] = useState<string>('');
    const [hasFiltered, setHasFiltered] = useState<boolean>(false);

    const initialFormState = () => ({
        id: 0,
        shippingCompanyId: 0,
        shippingCompanyName: '',
        desi: 0,
        cost: 0,
        startDate: TimeHelper.formatDate(new Date()),
        endDate: TimeHelper.formatDate(new Date()),
        changedAt: null,
        changedBy: '',
    });

    const fetchList = useCallback(() => {
        setIsLoading(true);
        const companyParam = {
            ShippingCompanyId: filterCompanyId,
            StartDate: filterStart?.split('/').reverse().join('-') || '',
            EndDate: filterEnd?.split('/').reverse().join('-') || '',
        };
        ShippingQuery.GetShippingCompanyCostForDesi(companyParam)
            .then((res: any) => {
                const data = res?.getShippingCompanyCostForDesiResponseItems || [];
                const dataWithId = data?.map((item: any, index: number) => ({
                    ...item,
                    id: index + 1,
                }));
                setRows(dataWithId);
                setHasFiltered(true);
            })
            .catch(() => {
                Swal.fire(t('general.error'), '', 'error');
            })
            .finally(() => setIsLoading(false));
    }, [filterCompanyId, filterStart, filterEnd, t, companyOptions]);

    const resetForm = () => {
        setFormState(initialFormState());
    };

    const handleOpenAdd = () => {
        setIsEditMode(false);
        resetForm();
        setDrawerOpen(true);
    };

    const handleOpenEdit = (row: FeeRowItem) => {
        setIsEditMode(true);
        setFormState({ ...row });
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        resetForm();
    };

    const handleExportExcel = () => {
        const excelRows = rows.map((r) => ({
            shippingCompanyName: r.shippingCompanyName,
            desi: r.desi,
            cost: r.cost,
            startDate: TimeHelper.formatDate(r.startDate),
            endDate: TimeHelper.formatDate(r.endDate),
            changedAt: TimeHelper.formatDate(r.changedAt),
            changedBy: r.changedBy,
        }));
        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        const fileName = `LCWKargoÜcretYönetimi_${dd}.${mm}.${yyyy}.xlsx`;
        downloadExcel({
            excelDownloadData: excelRows,
            excelDownloadFolderName: 'LCW-Cargo-Fee-Management',
            fileNameOverride: fileName,
        });
    };

    const columns: ExtendedGridColDef[] = [
        {
            field: 'id',
            headerName: t('lcwCargoFeeManagement.list.columns.id'),
            flex: 0.5,
            hide: true,
        },
        {
            field: 'shippingCompanyName',
            headerName: t('lcwCargoFeeManagement.list.columns.cargoCompany'),
            flex: 1,
        },
        {
            field: 'desi',
            headerName: t('lcwCargoFeeManagement.list.columns.desi'),
            flex: 0.7,
        },
        {
            field: 'cost',
            headerName: t('lcwCargoFeeManagement.list.columns.cost'),
            width: 140,
            renderHeader: () => (
                <Box className={LCWFEE.costColumnHeader} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography component="span">{t('lcwCargoFeeManagement.list.columns.cost')}</Typography>
                    <Tooltip title={t('lcwCargoFeeManagement.list.columns.costInfo')} placement="top">
                        <InfoOutlinedIcon className={LCWFEE.inputIconCostInfo} sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Tooltip>
                </Box>
            ),
            renderCell: (params: any) => <Typography sx={cellTypographySx}>{`${params.row.cost} ₺`}</Typography>,
        },
        {
            field: 'startDate',
            headerName: t('lcwCargoFeeManagement.list.columns.startDate'),
            flex: 1,
            renderCell: (params: any) => (
                <Typography sx={cellTypographySx}>{TimeHelper.formatDate(params.row.startDate)}</Typography>
            ),
        },
        {
            field: 'endDate',
            headerName: t('lcwCargoFeeManagement.list.columns.endDate'),
            flex: 1,
            renderCell: (params: any) => (
                <Typography sx={cellTypographySx}>{TimeHelper.formatDate(params.row.endDate)}</Typography>
            ),
        },
        {
            field: 'changedAt',
            headerName: t('lcwCargoFeeManagement.list.columns.changedAt'),
            flex: 1,
            renderCell: (params: any) => (
                <Typography sx={cellTypographySx}>{TimeHelper.formatDate(params.row.changedAt)}</Typography>
            ),
        },
        {
            field: 'changedBy',
            headerName: t('lcwCargoFeeManagement.list.columns.changedBy'),
            flex: 1,
        },
        {
            field: 'actions',
            headerName: t('lcwCargoFeeManagement.list.columns.edit'),
            flex: 0.8,
            renderCell: (params: any) => (
                <Box className={LCWFEE.editCellWrap} sx={{ width: '100%', display: 'flex', height: '100%', alignItems: 'center' }}>
                    <Button
                        className={LCWFEE.inputButtonRowEdit}
                        onClick={() => handleOpenEdit(params.row)}
                        sx={{
                            minWidth: '30px !important',
                            width: '30px !important',
                            minHeight: '30px !important',
                            height: '30px !important',
                            backgroundColor: '#00B8F4',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'none',
                            p: 0,
                            m: 0,
                            '&:hover': {
                                backgroundColor: '#0099c8',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <EditIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Button>
                </Box>
            ),
        },
    ];

    if (!hasFiltered) {
        return (
            <Box className={LCWFEE.listEmptyStateWrap} sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
                <Paper
                    className={LCWFEE.listEmptyStatePaper}
                    sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: 420 }}
                >
                    <SCUINoRecordComponent />
                    <FormControl className={LCWFEE.listFilterCompanyWrap} fullWidth>
                        <InputLabel id="filter-company-label">
                            {t('lcwCargoFeeManagement.list.columns.cargoCompany')}
                        </InputLabel>
                        <Select
                            className={LCWFEE.inputSelectFilterCompany}
                            labelId="filter-company-label"
                            label={t('lcwCargoFeeManagement.list.columns.cargoCompany')}
                            value={filterCompanyId}
                            onChange={(e) => setFilterCompanyId(Number(e.target.value))}
                        >
                            {(companyOptions || []).map((opt) => (
                                <MenuItem key={opt.id} value={opt.id}>
                                    {opt.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box className={LCWFEE.listFilterDateWrap} sx={{ width: '100%' }}>
                        <SCUIDateRanger
                            fromDate={filterStart}
                            toDate={filterEnd}
                            setDateRange={(range: any) => {
                                setFilterStart(range[0] || '');
                                setFilterEnd(range[1] || '');
                            }}
                            dateFormat="DD/MM/YYYY"
                            containerStyling={{ width: '100%' }}
                        />
                    </Box>
                    <SCUIButton
                        className={LCWFEE.inputButtonFilter}
                        text={t('general.filter')}
                        type="primary"
                        sizeType="fixed"
                        size={'m'}
                        onClick={fetchList}
                    />
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Box className={LCWFEE.listToolbarBackWrap} sx={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', marginBottom: '12px' }}>
                <SCUIButton
                    className={LCWFEE.inputButtonBack}
                    text={t('general.back')}
                    type="secondary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={() => setHasFiltered(false)}
                    showStartIcon
                    startIcon={<ArrowBackIcon sx={{ color: '#0d6efd' }} />}
                />
            </Box>
            <Box className={LCWFEE.listToolbarActionsWrap} sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
                <SCUIButton
                    className={LCWFEE.inputButtonAddNew}
                    text={t('lcwCargoFeeManagement.list.addNew')}
                    type="primary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={handleOpenAdd}
                    showStartIcon
                    startIcon={<AddIcon sx={{ color: '#fff' }} />}
                />
                <SCUIButton
                    className={LCWFEE.inputButtonExportExcel}
                    text={t('general.exportToExcel')}
                    type="secondary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={handleExportExcel}
                    showStartIcon
                    startIcon={<FileDownloadIcon sx={{ color: '#0d6efd' }} />}
                />
            </Box>

            <Box className={LCWFEE.listGridWrap}>
            <SCUICustomDataGrid rows={rows} columns={columns} />
            </Box>

            <FeeManagementListDrawer
                open={drawerOpen}
                onClose={handleCloseDrawer}
                companyOptions={companyOptions}
                initialData={isEditMode ? (formState as any) : undefined}
                setIsLoading={setIsLoading}
                onSaved={fetchList}
            />
        </Box>
    );
};
